import {ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {BehaviorSubject, filter, map, Observable, Subject, switchMap, take, takeUntil} from 'rxjs';
import {dispatchDeletedItemEvent, dispatchSavedItemEvent, SearchCriteria, SearchResult} from '@snelstart/domain-logic';
import {SharedUiTableModule, TableConfiguration, ZeroStateConfiguration} from '@snelstart/ui-table';
import {
  BokkzAlgDataService,
  BokkzClient,
  BokkzSbDataService,
  BokkzSbLogic, FeatureServiceComponent,
  GiftTaxEntity,
  MenuInterface,
  SbBerekening,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder} from '@angular/forms';
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-donation-list',
  templateUrl: './feature-tax-donation-list.component.html',
  styleUrl: './feature-tax-donation-list.component.scss',
  imports: [CommonModule, SharedUiTableModule, SharedUiButtonModule, FeatureServiceComponent],
})
export class FeatureTaxDonationListComponent implements OnInit, OnDestroy {

  @Output() public details = new EventEmitter<string>();
  private destroy$ = new Subject<void>();
  protected tableConfig!: TableConfiguration<any, any>;
  public dialogVisible = false;
  protected giftTaxId: any;
  protected clientId: any;
  public showTable = false;
  public year = {} as number;
  public options$ = new BehaviorSubject<MenuInterface[]>([]);
  public options = [] as MenuInterface[];
  public giftTaxEntity = {} as GiftTaxEntity;
  public berekening = {} as SbBerekening;
  public totaleSchenking = 0;
  public benutteVrijstellingen = 0;
  public totaleBelastbareSchenking = 0;
  public verschuldigdeSchenkbelasting = 0;
  public nameVerkrijger = '';
  public nameSchenker = '';
  public title = 'Aangifte';

  navigateToSubPage(pageMapping: string): void {
    const url = `/tax-donation/modules/${this.clientId}/${pageMapping}`
    this.router.navigate([url]).then(() => console.log('complete'));
  }

  constructor(
    public dataService: BokkzSbDataService,
    private algDataService: BokkzAlgDataService,
    private router: Router,
    private bokkzClient: BokkzClient,
    public fb: FormBuilder,
    private cd: ChangeDetectorRef,
    public sbService: BokkzSbLogic,
    private activatedRoute: ActivatedRoute,
  ) {
  }

  ngOnInit(): void {
    this.algDataService.selectedYear.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.year = value;
      this.showTable = false;
      setTimeout(() => {
        this.createTableConfig();
        this.showTable = true;
      }, 0);
    });
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.giftTaxId = params['giftTaxId'];
      this.clientId = params['clientId'];
    });
    this.dataService.giftTaxEntity.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.giftTaxEntity = value;
      if (value.aangifte != undefined && value.aangifte.begiftigde != undefined) {
        if (value.aangifte.begiftigde.voorletters != undefined && value.aangifte.begiftigde.tussenvoegsel != undefined) {
          this.nameVerkrijger = value.aangifte?.begiftigde?.voorletters + ' ' + value.aangifte?.begiftigde?.tussenvoegsel + ' ' + value.aangifte?.begiftigde?.achternaam;
        } else if (value.aangifte.begiftigde.voorletters != undefined) {
          this.nameVerkrijger = value.aangifte?.begiftigde?.voorletters + ' ' + value.aangifte?.begiftigde?.achternaam;
        } else if (value.aangifte.begiftigde.achternaam != undefined) {
          this.nameVerkrijger = value.aangifte?.begiftigde?.achternaam;
        }
      }
      if (value.aangifte != undefined && value.aangifte.schenker != undefined) {
        if (value.aangifte.schenker.voorletters != undefined && value.aangifte.schenker.tussenvoegsels != undefined) {
          this.nameSchenker = value.aangifte?.schenker?.voorletters + ' ' + value.aangifte?.schenker?.tussenvoegsels + ' ' + value.aangifte?.begiftigde?.achternaam;
        } else if (value.aangifte.schenker.voorletters != undefined) {
          this.nameSchenker = value.aangifte?.schenker?.voorletters + ' ' + value.aangifte?.schenker?.achternaam;
        } else if (value.aangifte.schenker.achternaam != undefined) {
          this.nameSchenker = value.aangifte?.schenker?.achternaam;
        }
      }
      if (
        this.nameSchenker != '' &&
        value.aangifte?.schenker?.achternaam &&
        this.nameVerkrijger != '' &&
        value.aangifte?.begiftigde?.achternaam
      ) {
        this.title = 'Schenking door ' + this.nameSchenker + ' aan ' + this.nameVerkrijger;
      }
    });
    setTimeout(() => {
      this.sbService.getValidation();
    }, 500);
    this.dataService.sbBerekening.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.berekening = value;
      this.totaleSchenking = value.totaleSchenking || 0;
      this.benutteVrijstellingen = (value.benutteVrijstellingen || 0) + (value.totaalVrijgesteldOndernemingsVermogen || 0);
      this.totaleBelastbareSchenking = value.totaleBelastbareSchenking || 0;
      this.verschuldigdeSchenkbelasting = value.verschuldigdeSchenkbelasting || 0;
    });
    this.dataService.menuInterface.pipe(takeUntil(this.destroy$)).subscribe((menuInterface) => {
      const old = this.options$.value;
      if (JSON.stringify(this.options$.value) != JSON.stringify(menuInterface)) {
        this.options$.next([...menuInterface.reverse()]);
        this.cd.detectChanges();
        for (let i = 0; i < menuInterface.length; i++) {
          if (JSON.stringify(old[i]) != JSON.stringify(menuInterface[i])) {
            dispatchSavedItemEvent({
              identifier: menuInterface[i].value,
              result: menuInterface[i] as unknown as Record<number, MenuInterface>,
            });
            if (!menuInterface[i].visible) {
              dispatchDeletedItemEvent({
                identifier: menuInterface[i].value,
              });
            }
          }
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createTableConfig(): void {
    const criteria = new SearchCriteria();

    this.tableConfig = TableConfiguration.create({
      identifier: 'value',
      options: {
        useSearch: false,
        useSort: true,
      },
      criteria: criteria,

      search: () => {
        return this.bokkzClient.getGiftTax(this.clientId, String(this.year)).pipe(
          switchMap((giftTaxEntity) => {
            if (giftTaxEntity.aangifte) {
              this.dataService.updateGiftTaxEntity(giftTaxEntity);
              this.sbService.getCalculation();
            } else {
              this.options$.next([]);
              this.dataService.updateAantalValidaties(0);
            }

            return this.options$.pipe(
              filter((options) => Array.isArray(options)),
              map((options) => {
                const sortedGroups = options
                  .filter((item) => item.visible)
                  .map((group) => ({
                    ...group,
                    subonderdelen: (group.subonderdelen || [])
                      .filter((sub) => sub.visible)
                      .sort((a, b) => a.value - b.value),
                  }))
                  .sort((a, b) => a.value - b.value); // Sorteer hoofdgroepen ook
                return new SearchResult(sortedGroups, sortedGroups.length);
              }),
              take(1),
            );
          }),
        );
      },

      details: (entity) => {
        if (entity) {
          this.details.next(entity.label);
        }
      },

      fromDetailsMap: (entity) => {
        return entity as unknown as Record<number, MenuInterface>;
      },

      zeroStateConfig: ZeroStateConfiguration.create({
        icon: 'book-open-cover',
        mainText: this.year < 2024
          ? 'Module niet beschikbaar'
          : $localize`Geen aangifte gevonden voor ` + this.year,
        subText: this.year < 2024
          ? 'De aangifte schenkbelasting wordt door SnelStart ondersteund vanaf fiscaal jaar 2024.'
          : 'Maak een nieuwe aangifte aan en start direct met invullen.',
        buttonText: this.year < 2024 ? undefined : `Maak nieuwe aangifte`,
        buttonConfig: this.year < 2024 ? undefined  : ButtonConfiguration.create({
          onClick: () => {
            return new Observable((subscriber) => {
              setTimeout(() => {
                subscriber.next((this.dialogVisible = true));
                subscriber.complete();
              }, 50);
            });
          },
        }),
      }),
    });
  }
}
